import { Component, Input, OnInit, AfterViewInit, ViewChild, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Campaign, CampaignAdminRequest, CampaignsCampaignAdminApiClient, CampaignsSafeBoxAdminApiClient } from 'src/app/api/api-campaigns.service.generated';
import { AdvertiserCampaignJsonEditComponent } from 'src/app/common/components/campaign-json-edit/campaign-json-edit.component';
import { ConfirmDialogModel, ButtonComponent } from 'wecheer-common';
import { UniversalCodeComponent } from './universal-code/universal-code.component';
import { PERMISSION } from 'src/app/const/permission';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FlexLayoutModule } from '@angular/flex-layout';
import { DatePipe, NgClass } from '@angular/common';
import { HasAnyPermissionDirective } from 'src/app/common/directives/has-any-permission/has-any-permission.directive';
import { lastValueFrom } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UserPermissionsService } from 'src/app/common/services/user-permissions.service';

@Component({
  selector: 'app-advertiser-campaigns',
  templateUrl: './advertiser-campaigns.component.html',
  styleUrls: ['./advertiser-campaigns.component.scss'],
  imports: [
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatSortModule,
    MatTooltipModule,
    FlexLayoutModule,
    DatePipe,
    NgClass,
    HasAnyPermissionDirective,
    ButtonComponent
  ]
})
export class AdvertiserCampaignsComponent implements OnInit, AfterViewInit {
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private snackbar = inject(MatSnackBar);
  private campaignsClient = inject(CampaignsCampaignAdminApiClient);
  private safeBoxAdminClient = inject(CampaignsSafeBoxAdminApiClient);
  private userPermissionsService = inject(UserPermissionsService);

  @ViewChild(MatSort) sort: MatSort;

  @Input() advertiserId: string;

  dataSource = new MatTableDataSource<Campaign>();
  isBrandAdmin = false;
  isLoading = false;
  now = new Date();

  readonly permissions = PERMISSION;

  async ngOnInit() {
    this.refresh();
    this.userPermissionsService.hasPermission(PERMISSION.BRANDADMIN).subscribe(isBrandAdmin => this.isBrandAdmin = isBrandAdmin);
  }

  ngAfterViewInit() {
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'createdDate': return item.createdAt?.getTime() ?? 0;
        case 'expire': return item.endDate ? item.endDate.getTime() : Number.MAX_SAFE_INTEGER;
        case 'description': return item.description ?? '';
        default: return (item as any)[property] ?? '';
      }
    };
    this.dataSource.sort = this.sort;
  }

  async refresh() {
    this.isLoading = true;
    const allCampaigns = await lastValueFrom(this.campaignsClient.getCampaignsForAdvertisers(this.advertiserId));

    // hide consumer referral campaigns
    this.dataSource.data = allCampaigns
      .filter(c => !c.isConsumerReferralStrategy)
      .filter(c => !c.isReferralCampaignStrategy)
      .sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0));
    this.isLoading = false;
  }

  async onAddJson() {
    const config = {
      ...AdvertiserCampaignJsonEditComponent.config,
      data: new CampaignAdminRequest({ advertiserId: this.advertiserId })
    };
    const edited = await lastValueFrom(this.dialog.open(AdvertiserCampaignJsonEditComponent, config).afterClosed());
    if (!edited)
      return;

    await this.refresh();
  }

  async onAddPunchCardCampaign() {
    this.router.navigate(['punch-card-campaign-edit', this.advertiserId, 'new']);
  }

  async onAddLuckyDrawCampaign() {
    this.router.navigate(['lucky-draw-campaign-edit', this.advertiserId, 'new']);
  }

  async onAddStaffCampaign() {
    this.router.navigate(['staff-campaign-edit', this.advertiserId, 'new']);
  }

  async onAddStaffPointCampaign() {
    this.router.navigate(['staff-point-campaign-edit', this.advertiserId, 'new']);
  }

  async onMultiCampaign() {
    this.router.navigate(['multi-campaign-edit', this.advertiserId, 'new']);
  }

  async onReferralCampaign() {
    this.router.navigate(['referral-campaign-edit', this.advertiserId, 'new']);
  }

  async onConsumerInfoCampaign() {
    this.router.navigate(['info-campaign-edit', this.advertiserId, 'new-c']);
  }

  async onStaffInfoCampaign() {
    this.router.navigate(['info-campaign-edit', this.advertiserId, 'new-s']);
  }

  async onOpenerCampaign() {
    this.router.navigate(['opener-campaign-edit', this.advertiserId, 'new']);
  }

  async onClone(campaign: Campaign) {
    const cloneQueryParams = { clone: 'true' };

    if (campaign.isReferralCampaignStrategy) { this.router.navigate(['info-campaign-edit', this.advertiserId, campaign.campaignId], { queryParams: cloneQueryParams }); return; }
    if (campaign.isPunchCardStrategy) { this.router.navigate(['punch-card-campaign-edit', this.advertiserId, campaign.campaignId], { queryParams: cloneQueryParams }); return; }
    if (campaign.isLuckyDrawStrategy) { this.router.navigate(['lucky-draw-campaign-edit', this.advertiserId, campaign.campaignId], { queryParams: cloneQueryParams }); return; }
    if (campaign.isStaffCampaignStrategy) { this.router.navigate(['staff-campaign-edit', this.advertiserId, campaign.campaignId], { queryParams: cloneQueryParams }); return; }
    if (campaign.isConsumerInformationStrategy) { this.router.navigate(['info-campaign-edit', this.advertiserId, campaign.campaignId], { queryParams: cloneQueryParams }); return; }
    if (campaign.isStaffInformationStrategy) { this.router.navigate(['info-campaign-edit', this.advertiserId, campaign.campaignId], { queryParams: cloneQueryParams }); return; }
    if (campaign.isStaffPointStrategy) { this.router.navigate(['staff-point-campaign-edit', this.advertiserId, campaign.campaignId], { queryParams: cloneQueryParams }); return; }
    if (campaign.isOpenerStrategy) { this.router.navigate(['opener-campaign-edit', this.advertiserId, campaign.campaignId], { queryParams: cloneQueryParams }); return; }

    const isMultiCampaign = !campaign.isPunchCardStrategy &&
      !campaign.isLuckyDrawStrategy &&
      !campaign.isStaffCampaignStrategy &&
      !campaign.isReferralCampaignStrategy &&
      !campaign.isConsumerInformationStrategy &&
      !campaign.isStaffInformationStrategy &&
      !campaign.isStaffPointStrategy &&
      !campaign.isOpenerStrategy;

    if (isMultiCampaign) {
      this.router.navigate(['multi-campaign-edit', this.advertiserId, campaign.campaignId], { queryParams: cloneQueryParams });
      return;
    }
  }

  async onEdit(campaign: Campaign) {

    if (campaign.isReferralCampaignStrategy) { this.router.navigate(['referral-campaign-edit', this.advertiserId, campaign.campaignId]); return; }
    if (campaign.isPunchCardStrategy) { this.router.navigate(['/punch-card-campaign-edit', this.advertiserId, campaign.campaignId]); return; }
    if (campaign.isLuckyDrawStrategy) { this.router.navigate(['lucky-draw-campaign-edit', this.advertiserId, campaign.campaignId]); return; }
    if (campaign.isStaffCampaignStrategy) { this.router.navigate(['staff-campaign-edit', this.advertiserId, campaign.campaignId]); return; }
    if (campaign.isConsumerInformationStrategy) { this.router.navigate(['info-campaign-edit', this.advertiserId, campaign.campaignId]); return; }
    if (campaign.isStaffInformationStrategy) { this.router.navigate(['info-campaign-edit', this.advertiserId, campaign.campaignId]); return; }
    if (campaign.isStaffPointStrategy) { this.router.navigate(['staff-point-campaign-edit', this.advertiserId, campaign.campaignId]); return; }
    if (campaign.isOpenerStrategy) { this.router.navigate(['opener-campaign-edit', this.advertiserId, campaign.campaignId]); return; }

    const isMultiCampaign = !campaign.isPunchCardStrategy &&
      !campaign.isLuckyDrawStrategy &&
      !campaign.isStaffCampaignStrategy &&
      !campaign.isReferralCampaignStrategy &&
      !campaign.isConsumerInformationStrategy &&
      !campaign.isStaffInformationStrategy &&
      !campaign.isStaffPointStrategy &&
      !campaign.isOpenerStrategy;

    if (isMultiCampaign) {
      this.router.navigate(['multi-campaign-edit', this.advertiserId, campaign.campaignId]); // go to edit page
      return;
    }
  }

  async onEditJson(campaign: Campaign) {
    const config = {
      ...AdvertiserCampaignJsonEditComponent.config,
      data: new CampaignAdminRequest({
        campaignId: campaign.campaignId,
        advertiserId: this.advertiserId,
        title: campaign.title,
        description: campaign.description,
        canJoin: campaign.canJoin,
        startDate: campaign.startDate,
        endDate: campaign.endDate,
        strategy: campaign.strategy,
        photoBannerUrl: campaign.photoBannerUrl,
        photoFullUrl: campaign.photoFullUrl,
        photoThumbUrl: campaign.photoThumbUrl,
        shouldAdvertise: campaign.shouldAdvertise
      })
    };
    const edited = await lastValueFrom(this.dialog.open(AdvertiserCampaignJsonEditComponent, config).afterClosed());
    if (!edited)
      return;

    await this.refresh();
  }

  async onRemove(campaign: Campaign) {
    const dialogModel = ConfirmDialogModel.danger(
      'This action cannot be undone. Are you absolutely sure?',
      'Delete Campaign?'
    );
    const confirmResult = await dialogModel.show(this.dialog);
    if (!confirmResult) return;

    await lastValueFrom(this.campaignsClient.deleteCampaign(campaign.id, campaign.advertiserId));

    this.snackbar.open(`Campaign was deleted!`, null, { duration: 3000 });

    this.refresh();
  }

  async onGenerateCode(campaign: Campaign) {
    const safeBox = await lastValueFrom(this.safeBoxAdminClient.createUniversalCode(campaign.campaignId));
    await lastValueFrom(this.dialog.open(UniversalCodeComponent, { data: safeBox }).afterClosed());
  }

  displayCampaignType(campaign: Campaign): string {
    const typeMap: Record<string, string> = {
      isLuckyDrawStrategy: 'L',
      isPunchCardStrategy: 'P',
      isStaffCampaignStrategy: 'S',
      isReferralCampaignStrategy: 'R',
      isConsumerInformationStrategy: 'IC',
      isStaffInformationStrategy: 'IS',
      isOpenerStrategy: 'O'
    };

    for (const [property, type] of Object.entries(typeMap)) {
      if (campaign[property as keyof Campaign]) return type;
    }

    return campaign.strategy ? 'M' : '?';
  }
}
